import { useState } from "react";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Reveal, SectionLabel, MaskLines } from "./Reveal";
import { mediaUrl } from "../../lib/api";
import { useContent } from "../../lib/content";

const CaseCard = ({ c, i, onOpen }) => (
  <Reveal delay={i * 0.08}>
    <button data-testid={`case-card-${i}`} onClick={() => onOpen(c)}
      className="group text-left w-full rounded-3xl border hairline bg-[#0b1222] overflow-hidden hover:border-[#ff5500]/40 transition-colors">
      {c.image && (
        <div className="aspect-[16/10] overflow-hidden">
          <img src={mediaUrl(c.image)} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
          {c.sector && <span className="text-[#7fd6fb]">{c.sector}</span>}
          {c.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.location}</span>}
        </div>
        <h3 className="mt-3 font-display-n font-bold text-white text-xl leading-snug">{c.title}</h3>
        {c.summary && <p className="mt-2 text-sm text-slate-300 line-clamp-2">{c.summary}</p>}
        {(c.results || []).length > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            {c.results.slice(0, 2).map((r, k) => (
              <div key={k} className="rounded-xl bg-white/[0.03] border hairline p-3">
                <p className="font-display font-extrabold text-2xl text-heat-grad">{r.value}</p>
                <p className="text-[12px] text-slate-400">{r.label}</p>
              </div>
            ))}
          </div>
        )}
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-white">Leggi il caso<ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
      </div>
    </button>
  </Reveal>
);

const Block = ({ title, text }) => (text ? <div><p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#ff8a4c] mb-2">{title}</p><p className="text-slate-300 leading-relaxed whitespace-pre-line">{text}</p></div> : null);

export const CaseStudies = ({ cases = [] }) => {
  const h = useContent().home;
  const [open, setOpen] = useState(null);
  if (!cases.length) return null;
  return (
    <section id="casi-studio" data-testid="cases-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><SectionLabel index="05" tone="heat">{h.cases_label}</SectionLabel></Reveal>
        <h2 className="mt-7 max-w-4xl font-display font-extrabold text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.02]">
          <MaskLines text={h.cases_title} />
        </h2>
        <Reveal><p className="mt-6 max-w-xl text-slate-300">{h.cases_text}</p></Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cases.map((c, i) => <CaseCard key={c.id} c={c} i={i} onOpen={setOpen} />)}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-lenis-prevent data-testid="case-dialog" className="max-w-3xl max-h-[88vh] overflow-y-auto bg-[#0b1222] border hairline text-white p-0">
          {open && (
            <div>
              {open.image && <img src={mediaUrl(open.image)} alt={open.title} className="w-full aspect-[16/8] object-cover" />}
              <div className="p-6 sm:p-8 space-y-6">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#7fd6fb]">{[open.sector, open.location, open.period].filter(Boolean).join(" · ")}</p>
                  <DialogTitle className="mt-2 font-display font-extrabold text-2xl sm:text-3xl">{open.title}</DialogTitle>
                  <DialogDescription className="mt-3 text-slate-300">{open.summary}</DialogDescription>
                </div>
                {(open.results || []).length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {open.results.map((r, k) => (
                      <div key={k} className="rounded-xl bg-white/[0.03] border hairline p-4">
                        <p className="font-display font-extrabold text-3xl text-heat-grad">{r.value}</p>
                        <p className="text-[13px] text-slate-400 mt-1">{r.label}</p>
                      </div>
                    ))}
                  </div>
                )}
                <Block title="Situazione di partenza" text={open.challenge} />
                <Block title="Cosa abbiamo fatto" text={open.solution} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
